import _ from 'lodash';
import Vue from "vue";
import router from '@/router';
import {BASE_API_URL, storage} from '@/store';
import {initFbSdk, fbLogout, getFbLoginStatus} from "@/utils/auth/facebookAuth.js";
import Gapi from '@/utils/auth/googleAuth.js';
import {createAuthAttributes} from '@/utils/auth/createAuthAttributes';
import {firebaseAuth} from '@/firebase'
import {deleteToken} from '@/firebase/firebase-messaging'
import {ga} from '@/utils/ga'
import {getUrlParameter} from '@/utils/urlHelpers'
import {setAppLanguage} from "@/utils";

const auth = {
  state: {
    access_token: null,
    isAuthenticated: false,
    socialMediaPlatformLogoutLoading: false,
    verificationAttributes: [],
    otpAccessToken: null,
    autoLogout: null,
    openId: {
      redirect_uri: undefined,
      client_id: undefined,
      state: undefined,
      scope: undefined,
      response_type: undefined,
      reponse_mode: undefined,
      nonce: undefined
    },
  },
  mutations: {
    setAccessToken(rootState, payload) {
      console.debug('setAccessToken', payload);
      rootState.access_token = payload;
    },
    setIsAuthenticated(state, payload) {
      console.debug('setIsAuthenticated', payload);
      state.isAuthenticated = payload;
      // load google-analytics when appuser is logged in
      ga();
    },
    setSocialMediaPlatformLogoutLoading(state, payload) {
      state.socialMediaPlatformLogoutLoading = payload;
    },
    SET_VERIFICATION_ATTRIBUTES(state, payload) {
      state.verificationAttributes = payload;
    },
    setOtpAccessToken(state, payload) {
      state.otpAccessToken = payload
    },
    setAutoLogout(state, payload) {
      if (_.isNull(payload)) {
        state.autoLogout = null
      } else {
        const time = parseInt(payload) * 1000 // Use milliseconds
        state.autoLogout = _.isNumber(time) ? time : null
      }
    },
    setOpenIdParams(state, payload) {
      const openIdParams = Object.keys(state.openId);
      for (let param of openIdParams) {
        if (payload[param]) {
          state.openId[param] = payload[param];
        }
      }
    }
  },
  actions: {
    async signUp({dispatch, rootState}, payload) {
      try {
        await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/create/?client_id=${rootState.client_id}`,
          {
            ...payload,
            locale: rootState.locale
          }
        )

        await dispatch('userLogin', {
          email: payload.email,
          password: payload.password,
          recaptchaResponse: payload.recaptchaResponse
        })
      } catch (error) {
        throw error.response.data
      }
    },
    async checkIdentifier({rootState}, payload) {
      try {
        await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/check-identifier/?client_id=${rootState.client_id}`,
          {
            ...payload,
            locale: rootState.locale
          }
        )
        // Valid identifier and user already registered
        return {
          isRegistered: true,
          error: true,
          code: 'USER_ALREADY_EXISTS',
        };
      } catch (error) {
        // Valid identifier and user not found
        if (error.response.status === 404) {
          return {
            isRegistered: false,
            error: false
          };
        } else {
          return {
            isRegistered: false,
            error: true
          };
        }
      }
    },
    // currently, this function is used wallet-pass only
    async deviceLogin({commit, rootState}, payload) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/device-login/?client_id=${rootState.client_id}`,
          {
            ...payload,
            locale: rootState.locale
          }
        )
        const accessToken = response.data.access_token
        if (response.data) {
          commit('setAccessToken', accessToken)
        }
      } catch (error) {
        throw error.response
      }
    },
    async directLinkLogin({dispatch, commit, rootState}, {magic_link_token}) {
      try {
        let response = await Vue.axios.post(`${BASE_API_URL}/api/v1/users/magic-link/`, {
          "magic_link_token": magic_link_token,
          "client_id": this.state.client_id,
          "locale": rootState.locale
        });
        commit('setAccessToken', response.data.access_token);
        storage.setItem('accessToken', response.data.access_token);
        await dispatch('fetchCustomerRelationship');
        commit('setIsAuthenticated', true);
        commit(
          'SET_VERIFICATION_ATTRIBUTES',
          createAuthAttributes({signupMode: false})
        );
      } catch (error) {
        throw {
          error: "LOGIN_FAILED"
        }
      }
    },
    async externalLogin({dispatch, rootState}, token) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/login/`,
          {
            auth_method: rootState.clientSlug,
            token,
            client_id: rootState.client_id,
            locale: rootState.locale
          }
        )
        const accessToken = response.data.access_token;
        await dispatch('userAuthenticate', {
          access_token: accessToken
        })
        return {ok: true}
      } catch (error) {
        throw {
          error: 'EXTERNAL_LOGIN_FAILED'
        }
      }
    },
    async loginWithConfig({dispatch, rootState}, payload) {
      try {
        const loginOnlyParam = payload.login_only
          ? `&login_only=${payload.login_only}`
          : ''

        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/login/?auth_method=${payload.method}${loginOnlyParam}&client_id=${rootState.client_id}`,
          {
            ...payload.token,
            locale: rootState.locale
          }
        )

        const accessToken = response.data.access_token

        await dispatch('userAuthenticate', {
          access_token: accessToken
        })
        return {ok: true}
      } catch (error) {
        throw error.response.data
      }
    },
    async customerLogin(
      {dispatch, rootState, state},
      {authName, payload}
    ) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/customer-login/?auth_name=${authName}&client_id=${rootState.client_id}`,
          {
            ...state.otpAccessToken,
            ...payload,
            locale: rootState.locale
          }
        )

        const accessToken = response.data.access_token

        await dispatch('userAuthenticate', {
          access_token: accessToken
        })
        return {ok: true}
      } catch (error) {
        throw {
          error: 'CUSTOMER_LOGIN_FAILED'
        }
      }
    },
    async userLogin({commit, dispatch, rootState}, {email, password, recaptchaResponse}) {
      try {
        let response = await Vue.axios.post(`${BASE_API_URL}/api/v1/users/token/`, {
          username: email,
          password: password,
          recaptchaResponse: recaptchaResponse,
          client_id: rootState.client_id
        });
        let access_token = response.data.access_token;

        await dispatch('userAuthenticate', {access_token});
      } catch (error) {
        console.error('Could not login', error);
        commit('setAccessToken', null);
        commit('setUser', null);
        commit('setIsAuthenticated', false);
        throw {
          error: "LOGIN_FAILED"
        };
      }
    },
    async userAuthenticate({commit, dispatch, rootState}, {access_token}) {
      commit('setAccessToken', access_token);
      try {
        await dispatch('fetchCustomerRelationship');
        commit('setIsAuthenticated', true);
        storage.setItem('accessToken', access_token);
        commit(
          'SET_VERIFICATION_ATTRIBUTES',
          createAuthAttributes({signupMode: false})
        );
        await dispatch('handleAutoLogoutParam');
        await dispatch('validateSession');

        const userLocale = _.get(rootState, 'user.locale', null)

        if (userLocale) {
          await setAppLanguage(userLocale)
          await dispatch('fetchInitialData')
        } else {
          await setAppLanguage()
        }

      } catch (error) {
        storage.removeItem('accessToken');
        dispatch('userSignOut');
      }
    },
    async requestOTP({rootState}, phoneNumber) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/request-otp/`, {
            client_id: rootState.client_id,
            phone_number: phoneNumber
          }
        )

        return response.data
      } catch (error) {
        throw {
          error: 'REQUEST_OTP_FAILED'
        };
      }
    },
    async requestIdentifierOTP({rootState}, payload) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/request-otp/`, {
            client_id: rootState.client_id,
            ...payload
          }
        )

        return response
      } catch (error) {
        throw {
          error: 'REQUEST_OTP_FAILED'
        };
      }
    },
    async verifyEmailOTP(
      {rootState, state, commit, dispatch},
      {email, verificationCode, loginOnly}
    ) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/users/verify-otp/`, {
            params: {
              client_id: rootState.client_id,
              email: email,
              otp: verificationCode
            }
          }
        )

        commit('setOtpAccessToken', response.data)

        await dispatch('loginWithConfig', {
          method: 'email_login',
          token: state.otpAccessToken,
          login_only: loginOnly
        })
        return {ok: true}
      } catch (error) {
        throw error
      }
    },
    async verifyOTP(
      {rootState, state, commit, dispatch},
      {phoneNumber, verificationCode, loginOnly, skipVerify}
    ) {
      try {
        if (!skipVerify) {
          const response = await Vue.axios.get(
            `${BASE_API_URL}/api/v1/users/verify-otp/`, {
              params: {
                client_id: rootState.client_id,
                phone_number: phoneNumber,
                otp: verificationCode
              }
            }
          )

          commit('setOtpAccessToken', response.data)
        }

        await dispatch('loginWithConfig', {
          method: 'sms_login',
          token: state.otpAccessToken,
          login_only: loginOnly
        })
        return {ok: true}
      } catch (error) {
        throw error
      }
    },
    async firebaseRequestOTP(
      _,
      {phoneNumber, recaptchaVerifier}
    ) {
      try {
        const confirmationResult = await firebaseAuth
          .signInWithPhoneNumber(phoneNumber, recaptchaVerifier)

        return confirmationResult
      } catch (error) {
        throw {
          error: 'FIREBASE_REQUEST_OTP_FAILED'
        }
      }
    },
    async firebaseVerifyOTP(
      {state, commit, dispatch},
      {confirmationResult, verificationCode, loginOnly, skipVerify}
    ) {
      try {
        if (!skipVerify) {
          const result = await confirmationResult.confirm(
            verificationCode
          )
          const user = result.user

          if (user) {
            const idToken = await user.getIdToken(true)

            if (idToken) {
              commit('setOtpAccessToken', {firebase_phone_auth_token: idToken})
              await dispatch('googleSignin', {
                idToken,
                loginOnly: loginOnly || false
              })
              return {ok: true}
            } else {
              throw {
                error: 'FIREBASE_FETCH_ID_TOKEN_FAILED'
              }
            }
          } else {
            throw {
              error: 'FIREBASE_FETCH_USER_FAILED'
            }
          }
        } else {
          await dispatch('googleSignin', {
            idToken: state.otpAccessToken.firebase_phone_auth_token,
            loginOnly: loginOnly || false
          })
        }
      } catch (error) {
        if (error.code) {
          throw error
        }
        throw {
          error: 'FIREBASE_VERIFY_OTP_FAILED'
        }
      }
    },
    async googleSignin({rootState, dispatch}, payload) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v2/users/google-signin/?client_id=${rootState.client_id}`, {
            firebase_token: payload.idToken,
            login_only: payload.loginOnly,
            locale: rootState.locale
          }
        )

        if (response.data) {
          const accessToken = response.data.access_token
          await dispatch('userAuthenticate', {
            access_token: accessToken
          })
          return {ok: true}
        } else {
          throw {
            error: 'GOOGLE_SIGNIN_FAILED'
          }
        }
      } catch (error) {
        if (error.response) {
          // Error management
          if (error.response.status === 403 && _.get(error.response, 'data.detail', '') === 'The current user is not authenticated') {
            throw {
              code: 'USER_DOES_NOT_EXIST'
            }
          }
        }
        throw {
          error: 'GOOGLE_SIGNIN_FAILED'
        }
      }
    },
    async verifyEmail() {
      try {
        await Vue.axios.post(
          `${BASE_API_URL}/api/v1/users/request-email-activation/`
        )
      } catch (error) {
        throw {
          error: "REQUEST_EMAIL_VERIFICATION_FAILED"
        }
      }
    },
    async validateSession({rootState, dispatch}) {
      try {
        const response = await Vue.axios.get(
          `${BASE_API_URL}/api/v1/users/validate-session/?client_id=${rootState.client_id}`
        )

        const isValid = _.get(response, 'data.valid', false)

        if (!isValid) {
          dispatch('userSignOut')
        }
      } catch (error) {
        throw {
          error: "VALIDATE_SESSION_FAILED"
        }
      }
    },
    async requestResetPassword({rootState}, email) {
      try {
        const response = await Vue.axios.post(
          `${BASE_API_URL}/api/v1/clients/${rootState.client_id}/request-password-reset/`,
          {
            email
          }
        )

        return response.data
      } catch (error) {
        return {
          error: "REQUEST_RESET_PASSWORD_FAILED"
        };
      }
    },
    async userSignOut({rootGetters, commit, dispatch}) {
      if (_.get(rootGetters.extensionSettings, 'auth.enable_facebook_login') &&
          _.get(rootGetters.extensionSettings, 'facebook.app_id')) {
        await dispatch('facebookLogout');
      }

      commit('setAccessToken', null);
      commit('setUser', null);
      commit('setIsAuthenticated', false);
      commit('setOtpAccessToken', null);
      commit('setAutoLogout', null);

      storage.clear();
      deleteToken();
      router.push({name: 'signin'});
    },
    async facebookLogout({commit}) {
      commit('setSocialMediaPlatformLogoutLoading', true);
      try {
        if (!window.FB) {
          await initFbSdk();
        }
        let status = await getFbLoginStatus();
        if (status.status === 'connected') {
          await fbLogout();
        }
      } catch (error) {
        console.error('Error logging out from facebook', error);
      } finally {
        commit('setSocialMediaPlatformLogoutLoading', false);
      }
    },
    async googleLogout({commit}) {
      commit('setSocialMediaPlatformLogoutLoading', true);
      try {
        if (!window.gapi) {
          await Gapi.load();
        }
        if (Gapi.isSignedIn()) {
          Gapi.signOut();
        }
      } catch (error) {
        console.error('Error logging out from google', error);
      } finally {
        commit('setSocialMediaPlatformLogoutLoading', false);
      }
    },
    async handleAutoLogoutParam({state, commit, dispatch}) {
      try {
        const autoLogout = getUrlParameter('auto-logout')

        // If auto-logout param is not present in the url try to get the value from local storage instead
        if (_.isBoolean(autoLogout) && !autoLogout) {
          const savedValue = await dispatch('getAutoLogout')

          if (savedValue && state.isAuthenticated) {
            commit('setAutoLogout', savedValue)
          }
        }

        // If auto-logout param is present and it has a value save it in local storage and in the store
        if (_.isString(autoLogout) && !_.isEmpty(autoLogout)) {
          await dispatch('storeAutoLogout', autoLogout)
        }

        // If auto-logout param is present but the value is empty remove it entirely
        if (_.isString(autoLogout) && _.isEmpty(autoLogout)) {
          await dispatch('removeAutoLogout')
        }
      } catch (error) {
        console.log(error)
      }
    },
    async storeAutoLogout({state, commit}, time) {
      try {
        if (!_.isEmpty(time)) {
          if (state.isAuthenticated) {
            commit('setAutoLogout', time)
          }

          await storage.setItem('autoLogout', time)
        }
      } catch (error) {
        console.log(error)
      }
    },
    async getAutoLogout() {
      try {
        const value = await storage.getItem('autoLogout')
        return value
      } catch (error) {
        console.log(error)
      }
    },
    async removeAutoLogout() {
      try {
        await storage.removeItem('autoLogout')
      } catch (error) {
        console.log(error)
      }
    },
    async setAuthParams({state, commit}, payload) {
      if (!payload.redirect_uri && !state.openId.redirect_uri) {
        console.log(payload, state.openId);
        console.error('Missing redirect_uri');
        return
      }
      if (!payload.client_id && !state.openId.client_id) {
        console.error('Missing client_id');
        return
      }
      commit('setOpenIdParams', payload)
    },
    async manageOpenIdLogin({state, dispatch}, {handleAuthenticationOrRedirect}) {

      /** Helper function:
       *  properties and function to remove auth token listeners
       *  when not in use anymore */
      let listeners = []
      const removeTokenListeners = () => {
        listeners.forEach((listener) => {
          window.removeEventListener('message', listener)
        })
      }

      /** Helper function:
       *  Receives message from parent app (ReactNativeWebView : getAccessToken)
       *  to check if the user is already authenticated
       *  Yes -> manageOpenIdRedirect
       *  No -> redirect to signin */
      const getAccessTokenHandler = async(e) => {
        if (e.data.action === 'getAccessToken' && e.data.success && e.data.token) {
          await dispatch('userAuthenticate', {access_token: e.data.token})
          handleAuthenticationOrRedirect()
          removeTokenListeners()
        }
      }

      // If the user is not authenticated, check if the parent app is already authenticated
      if (!state.isAuthenticated && window.ReactNativeWebView) {
        /* On a mobile webview (ReactNativeWebView)
         -> Check if already authenticated on the parent app (getAccessToken)
         -> on listener event -> verify authentication -> handleAuthenticationOrRedirect
         -> track listeners to be removed */
        let docTokenListener = document.addEventListener('message', getAccessTokenHandler);
        let windowTokenListener = window.addEventListener('message', getAccessTokenHandler);
        listeners.push(docTokenListener, windowTokenListener)
        window.ReactNativeWebView.postMessage(
          JSON.stringify({action: 'getAccessToken'})
        );
      } else {
        // On a web browser
        // -> handleAuthenticationOrRedirect directly
        handleAuthenticationOrRedirect();
      }
    },
    async manageOpenIdRedirect({state, getters}) {
      try {
        // 1 - Check if basic params are set
        if (!getters.isOpenIdProvider) {
          return
        }
        // 2 - Check if user is authenticated
        if (!getters.isAuthenticated) {
          console.error('User not authenticated');
          return
        }

        // 3 - Obtain the authorization code/token
        let params = {
          params: {
            Authorization: `Token ${state.access_token}`,
            Host: 'api.helloagain.at',
            redirect: false,
            ...state.openId
            }
        };


        // 4 - Call the API
        // We handle the redirect manually
        let response = await Vue.axios.get('/api/v1/o/oauth2/authorize-logic/', params);
        const redirectUrl = _.get(response, 'data.redirect_uri', null);
        if (redirectUrl) {
          window.location.href = redirectUrl;
        } else {
          console.error('No redirect_uri found');
        }


      } catch (error) {
        if (error.response) {
          // Error management
          if (error.response.status === 401) {
            console.error('Error 401: Unauthorized', error);
          } else if (error.response.status === 404) {
            console.error('Error 404: Resource not found', error);
          } else {
            console.error('HTTP error', error.response.status, error);
          }
        } else if (error.request) {
          // The request was made but no response was received
          console.error('No response received', error);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error('Error managing OpenID redirect', error);
        }
      }
    }
  },
  getters: {
    isAuthenticated(state) {
      return state.isAuthenticated;
    },
    verificationAttributes(state) {
      return state.verificationAttributes;
    },
    otpAccessToken(state) {
      return state.otpAccessToken;
    },
    isUserVerified(state, getters, rootState, rootGetters) {
      const user = rootGetters.user;
      const isEmailVerified = _.get(
        user,
        'notificationpreferences.is_email_verified'
      );
      const isPhoneNumberVerified = _.get(
        user,
        'notificationpreferences.is_phone_number_verified'
      );

      return isEmailVerified || isPhoneNumberVerified;
    },
    isEmailVerificationRequired(state, getters, rootState, rootGetters) {
      const emailVerificationRequired = _.get(
        rootGetters.authSettings,
        'require_email_verification',
        false
      )

      if (!emailVerificationRequired) {
        return false
      }

      return !getters.isUserVerified
    },
    autoLogout(state) {
      return state.autoLogout
    },
    isOpenIdProvider(state) {
      if (_.get(state.openId, 'redirect_uri', null) === null) {
        console.error('Missing redirect_uri');
        return false;
      }
      if (_.get(state.openId, 'client_id', null) === null) {
        console.error('Missing client_id');
        return false;
      }
      return true
    }
  }
}


export default auth;
